import React, { useEffect, useMemo, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * 2-step Room wizard.
 *
 * Step 1  – Name, Capacity, Description.
 * Step 2  – Services grouped by category ▸ subcategory with collapse + "check all/uncheck all".
 *           Services with a matching subcategory_id are nested; services without one fall
 *           under an "Uncategorised" bucket per category.
 */
export default function RoomWizard({ open, onOpenChange, bid, room, onSaved }) {
  const isEdit = Boolean(room);
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState(1);
  const [status, setStatus] = useState("working");
  const [description, setDescription] = useState("");
  const [serviceIds, setServiceIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setName(room?.name || "");
    setCapacity(Number(room?.capacity ?? 1));
    setStatus(room?.status || "working");
    setDescription(room?.description || "");
    setServiceIds(Array.isArray(room?.service_ids) ? [...room.service_ids] : []);
    (async () => {
      try {
        const [cats, svcs] = await Promise.all([
          staffApi.get("/categories"),
          staffApi.get("/services", { params: { branch_id: bid } }),
        ]);
        setCategories(cats.data || []);
        setServices((svcs.data || []).filter((s) => s.active !== false));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [open, room]);

  const grouped = useMemo(() => buildGrouped(categories, services), [categories, services]);
  const selected = useMemo(() => new Set(serviceIds), [serviceIds]);

  const toggle = (id) => {
    setServiceIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const bulkSet = (ids, on) => {
    setServiceIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return Array.from(next);
    });
  };

  const canNext = name.trim().length > 0 && Number(capacity) >= 1;

  const save = async () => {
    if (!canNext) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        capacity: Math.max(1, parseInt(capacity, 10) || 1),
        status,
        description: description.trim(),
        service_ids: serviceIds,
        active: room?.active !== false,
      };
      let res;
      if (isEdit) {
        res = await staffApi.put(`/rooms/${room.id}`, payload);
        toast.success("Room updated");
      } else {
        res = await staffApi.post(`/branches/${bid}/rooms`, payload);
        toast.success("Room added");
      }
      const conflicts = res?.data?.conflicts;
      if (Array.isArray(conflicts) && conflicts.length > 0) {
        const summary = conflicts
          .slice(0, 3)
          .map((c) => `${c.date} · ${c.over_by} to reschedule`)
          .join("\n");
        const extra = conflicts.length > 3 ? `\n… + ${conflicts.length - 3} more day(s)` : "";
        toast.error(
          `Conflict Detected: Branch is over-booked for ${name.trim()}.\n${summary}${extra}`,
          { duration: 10000, id: `room-conflict-${room?.id || name.trim()}` },
        );
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="room-wizard-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit room" : "New room"} · Step {step} of 2</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <div>
              <Label>Room name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Laser Suite 2"
                data-testid="room-wizard-name"
              />
            </div>
            <div>
              <Label>Capacity</Label>
              <Input
                type="number"
                min={1}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                data-testid="room-wizard-capacity"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum concurrent appointments this room can host at the same time. Bookings that would exceed the capacity are blocked automatically.
              </p>
            </div>
            <div>
              <Label>Status</Label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="room-wizard-status"
              >
                <option value="working">Working / Clean</option>
                <option value="out_of_service">Out of Service</option>
                <option value="maintenance">Maintenance</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Non-Working rooms are excluded from booking availability instantly across staff calendar and the client portal.
              </p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Notes on the room's purpose or equipment (optional)"
                rows={3}
                data-testid="room-wizard-description"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <p className="text-sm text-muted-foreground">
              Tick every treatment that can be performed in this room. Services with no room available cannot be booked online or by staff.
            </p>
            {grouped.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No services in the catalog yet.</p>
            )}
            {grouped.map((cat) => (
              <CategoryBlock
                key={cat.id || cat.name}
                cat={cat}
                selected={selected}
                onToggle={toggle}
                onBulk={bulkSet}
              />
            ))}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {step === 2 && (
              <Button variant="outline" onClick={() => setStep(1)} data-testid="room-wizard-back">
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            {step === 1 ? (
              <Button onClick={() => setStep(2)} disabled={!canNext} data-testid="room-wizard-next">Next</Button>
            ) : (
              <Button onClick={save} disabled={saving} data-testid="room-wizard-save">
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create room"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryBlock({ cat, selected, onToggle, onBulk }) {
  const [open, setOpen] = useState(true);
  const catIds = cat.allServiceIds;
  const allChecked = catIds.length > 0 && catIds.every((id) => selected.has(id));
  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid={`room-wizard-cat-${cat.name}`}>
      <div className="flex items-center justify-between bg-secondary/40 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 font-medium text-left flex-1"
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span>{cat.name}</span>
          <span className="text-xs text-muted-foreground">({catIds.length})</span>
        </button>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            onClick={() => onBulk(catIds, true)}
            className={"text-primary hover:underline " + (allChecked ? "opacity-40 pointer-events-none" : "")}
            data-testid={`room-wizard-check-all-${cat.name}`}
          >Check all</button>
          <span className="text-muted-foreground">·</span>
          <button
            type="button"
            onClick={() => onBulk(catIds, false)}
            className="text-muted-foreground hover:underline"
            data-testid={`room-wizard-uncheck-all-${cat.name}`}
          >Uncheck all</button>
        </div>
      </div>
      {open && (
        <div className="divide-y divide-border">
          {cat.subs.map((sub) => (
            <SubBlock key={sub.id || sub.name} sub={sub} selected={selected} onToggle={onToggle} onBulk={onBulk} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubBlock({ sub, selected, onToggle, onBulk }) {
  const [open, setOpen] = useState(true);
  const ids = sub.services.map((s) => s.id);
  const allChecked = ids.length > 0 && ids.every((id) => selected.has(id));
  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 bg-background">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-sm text-muted-foreground flex-1 text-left"
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <span>{sub.name}</span>
          <span className="text-xs">({ids.length})</span>
        </button>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            onClick={() => onBulk(ids, true)}
            className={"text-primary hover:underline " + (allChecked ? "opacity-40 pointer-events-none" : "")}
          >Check all</button>
          <span className="text-muted-foreground">·</span>
          <button
            type="button"
            onClick={() => onBulk(ids, false)}
            className="text-muted-foreground hover:underline"
          >Uncheck all</button>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-2">
          {sub.services.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">No services in this group.</p>
          )}
          <ul className="space-y-1">
            {sub.services.map((s) => {
              const on = selected.has(s.id);
              return (
                <li key={s.id} className="flex items-center gap-2 py-1">
                  <Checkbox
                    id={`svc-${s.id}`}
                    checked={on}
                    onCheckedChange={() => onToggle(s.id)}
                    data-testid={`room-wizard-svc-${s.id}`}
                  />
                  <label htmlFor={`svc-${s.id}`} className="text-sm cursor-pointer">
                    {s.name}
                    {s.duration_min ? <span className="text-xs text-muted-foreground ml-2">· {s.duration_min} min</span> : null}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function buildGrouped(categories, services) {
  // Group by category_id ⇒ subcategory_id. Fallback bucket: "Uncategorised".
  const catMap = new Map();
  categories.forEach((c) => {
    catMap.set(c.id, {
      id: c.id,
      name: c.name,
      subs: new Map(),
      allServiceIds: [],
    });
    (c.subcategories || []).forEach((sub) => {
      catMap.get(c.id).subs.set(sub.id, { id: sub.id, name: sub.name, services: [] });
    });
  });
  const uncategorised = { id: "__uncat__", name: "Uncategorised", subs: new Map(), allServiceIds: [] };

  services.forEach((s) => {
    const catId = s.category_id;
    const subId = s.subcategory_id;
    let cat = catId && catMap.get(catId);
    if (!cat) {
      // Fall back to name-based match, otherwise the catch-all bucket
      cat = [...catMap.values()].find((c) => c.name === (s.category || ""));
    }
    if (!cat) {
      cat = uncategorised;
    }
    let sub = subId && cat.subs.get(subId);
    if (!sub) {
      const label = subId ? "General" : (s.subcategory || "General");
      const subKey = `__sub_${label}`;
      sub = cat.subs.get(subKey);
      if (!sub) {
        sub = { id: subKey, name: label, services: [] };
        cat.subs.set(subKey, sub);
      }
    }
    sub.services.push(s);
    cat.allServiceIds.push(s.id);
  });

  const finalize = (c) => ({
    id: c.id,
    name: c.name,
    allServiceIds: c.allServiceIds,
    subs: [...c.subs.values()].filter((sub) => sub.services.length > 0),
  });
  const out = [...catMap.values()].filter((c) => c.allServiceIds.length > 0).map(finalize);
  if (uncategorised.allServiceIds.length > 0) out.push(finalize(uncategorised));
  return out;
}
